import { defineStore } from 'pinia'

import EventBus, { GlobalEvents } from '@shared/event-bus'
import { StoreState } from '@shared/models/interfaces/store'
import { Attachment } from '@shared/models/proto/base/attachment'
import { Message } from '@shared/models/proto/base/messages'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { updateState } from '@shared/utils/store/http-request-state'

import {
  AttachmentIndexDTO,
  CreateAttachmentDTO,
  ICreateAttachmentDTO,
} from '@pc/models/proto/dto/attachments'
import {
  AttachmentIndexRO,
  AttachmentItemRO,
} from '@pc/models/proto/ro/attachments'
import { useMessagesStore } from '@pc/store/messages'

interface State {
  records: Record<Message['id'], StoreState<Attachment>>
}

export const apiTransport = new ApiTransport(resourcePath, 'pc')

const state = (): State => ({
  records: {},
})

export const useMessageAttachmentsStore = defineStore('message-attachments', {
  state,

  actions: {
    async fetchAttachment(messageId: number) {
      const dto = new AttachmentIndexDTO({ messageId })
      const messagesStore = useMessagesStore()
      try {
        this.records[messageId] = updateState(this.records[messageId])

        const { data } = await apiTransport.get(
          '/attachments',
          dto,
          AttachmentIndexRO
        )

        if (data?.[0]) {
          this.records[messageId] = updateState(
            this.records[messageId],
            data[0]
          )
        } else {
          delete this.records[messageId]
        }
        messagesStore.updateAttachmentsById(messageId, data)
        const firstAttachment = data?.[0]

        firstAttachment?.quickSignDocument &&
          messagesStore.updateQuickSignDocumentInMessage(
            firstAttachment.quickSignDocument
          )
        return data
      } catch (e) {
        this.records[messageId] = updateState(
          this.records[messageId],
          new Error(`${e}`)
        )
        return null
      }
    },

    async createAttachment(params: ICreateAttachmentDTO) {
      const dto = new CreateAttachmentDTO(params)
      const { messageId } = dto
      try {
        this.records[messageId] = updateState(this.records[messageId])

        const { data } = await apiTransport.post(
          '/attachments/',
          dto,
          CreateAttachmentDTO,
          AttachmentItemRO,
          {
            onUploadProgress({ total, loaded }) {
              EventBus.emit(GlobalEvents.AsyncRequestProgress, {
                total,
                loaded,
              })
            },
          }
        )

        if (data) {
          this.records[messageId] = updateState(this.records[messageId], data)
        } else {
          delete this.records[messageId]
        }
      } catch (e) {
        this.records[messageId] = updateState(
          this.records[messageId],
          new Error(`${e}`)
        )
        throw e
      }
    },

    setPendingAttachment(messageId: Message['id']) {
      this.records[messageId] = updateState(this.records[messageId])
    },
  },

  getters: {
    getAttachment: (state) => (messageId: Message['id']) =>
      state.records[messageId]?.data || null,

    getIsLoading: (state) => (messageId: Message['id']) =>
      state.records[messageId]?.status.pending || false,
  },
})
